/* eslint-disable no-unused-vars */
import { Button, Empty, Spin } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { RiAiGenerate } from "react-icons/ri";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import SplineChart from "@/components/Charts/SplineChart";
import ReportTable from "@/components/Reports/ReportTable";
import { userRole, userRoleFormate } from "@/constant";
import { exportCSV } from "@/utils/exportCsv";
import { socket } from "@/api/socket";
import DeviceComparisonModal from "./DeviceComparisonModal";
import LoadingOverlay from "@/components/Shared/LoadingOverlay";
import { IoCloudDownloadOutline } from "react-icons/io5";
import { flare } from "@flareapp/js";

const DataAnalysis = () => {
  const { userDetails } = useSelector((state) => state.user);

  const [show, setShow] = useState(false);
  const [modalMode, setModalMode] = useState("generate"); // 'generate' or 'bulk'
  const [formData, setFormData] = useState({ type: "graph" });
  const [isLoading, setIsLoading] = useState(false);

  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  // data
  const [deviceData, setDeviceData] = useState([]);
  const [deviceTableData, setDeviceTableData] = useState([]);
  const [deviceParameters, setDeviceParameters] = useState([]);

  const handleDownloadReport = async () => {
    try {
      // Validate required fields for device comparison
      if (
        !formData.devices ||
        formData.devices.length === 0 ||
        !formData.timeRange ||
        formData.timeRange.length === 0
      ) {
        Swal.fire({
          icon: "warning",
          title: "Missing Information",
          text: "Please ensure devices and date range are selected before downloading the report.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      // Prepare request body
      const { timeRange, type, ...rest } = formData;
      const body = { ...rest };

      if (timeRange?.length) {
        body.from = moment(new Date(timeRange[0])).format("YYYY-MM-DD");
        body.to = moment(new Date(timeRange[1])).format("YYYY-MM-DD");
      }

      // Generate a unique session ID for tracking this download
      const sessionId = `download_device_comparison_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Show persistent processing toast
      Swal.fire({
        icon: "info",
        title: "Preparing Your Report",
        text: "Please wait while we prepare your download...",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Setup Socket.IO listener for progress updates
      const handleProgressUpdate = (data) => {
        if (data.sessionId === sessionId && data.type === "bulk_download_progress") {
          if (data.status === "processing") {
            // Update the existing toast with progress
            Swal.update({
              icon: "info",
              title: "Preparing Your Report",
              text: `Processing ${data.totalRecords?.toLocaleString() || 0} records...`,
            });
          } else if (data.status === "completed") {
            // Remove the listener
            socket.off("messageResponse", handleProgressUpdate);

            // Close the processing toast and show success message
            Swal.close();

            Swal.fire({
              icon: "success",
              title: "Download Complete!",
              text: `Successfully downloaded ${data.totalRecords?.toLocaleString() || 0} records`,
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
          } else if (data.status === "error") {
            // Remove the listener
            socket.off("messageResponse", handleProgressUpdate);

            // Close the processing toast and show error
            Swal.close();

            Swal.fire({
              icon: "error",
              title: "Download Failed",
              text: "Unable to complete the download. Please try again.",
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 4000,
              timerProgressBar: true,
            });
          }
        }
      };

      socket.on("messageResponse", handleProgressUpdate);

      // Add session ID for progress tracking
      body.sessionId = sessionId;

      // Start the bulk download process
      const response = await api.post(`${endpoint.device_comparison}?type=bulk&bulk=true`, body);

      // Process and download CSV automatically
      if (response && response.results) {
        // Process the bulk data for CSV export
        const processedData = response.results.map(({ _id, ...rowData }) => rowData);

        // Export to CSV immediately
        exportCSV({
          data: processedData,
          filename: `report-device-comparison-bulk-${moment().format("YYYY-MM-DD-HH-mm")}`,
        });

        // Log the activity
        const logBody = {
          business: userDetails.business,
          site: userDetails.site,
          activityCategory: "REPORT",
          activitySubcategory: "DOWNLOAD",
          activityMsg: `${userDetails.name} (${userRoleFormate[userDetails.role]}) has Downloaded Bulk Report (Device Comparison) - ${response.results.length} records`,
        };
        await api.post(endpoint.create_custom_log, logBody);
      }

      // Cleanup listener after a delay
      setTimeout(() => {
        socket.off("messageResponse", handleProgressUpdate);
      }, 10000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "An error occurred while downloading the report. Please try again.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
      });
    }
  };

  useEffect(() => {
    document.title = `${appConfig.name} - Parameter Comparison`;
  }, []);

  const getReport = async (paramsType) => {
    setIsLoading(true);
    const { timeRange, type, ...rest } = formData;

    const body = { ...rest };

    if (timeRange?.length) {
      body.from = moment(new Date(timeRange[0])).format("YYYY-MM-DD");
      body.to = moment(new Date(timeRange[1])).format("YYYY-MM-DD");
    }

    try {
      const response = await api.post(endpoint.device_comparison, body, { params: { limit, page, type: paramsType } });
      if (paramsType === "graph") {
        setDeviceData(response);
      } else {
        setDeviceTableData(response?.results);
        if (response?.deviceParameters) {
          setDeviceParameters(response?.deviceParameters);
        }
        setTotal(response?.count || 0);
      }
    } catch (error) {
      flare.report(error);
      setDeviceData([]);
      setDeviceTableData([]);
      setDeviceParameters([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (total && formData?.type === "table") {
      getReport("table");
    }
  }, [page, limit]);

  return (
    <LoadingOverlay isLoading={isLoading}>
      <div className="analysis">
        <div className="card p-3 m-md-3">
          <div className="d-flex flex-column flex-md-row g-3 align-items-md-center mb-3 justify-content-between">
            <div>
              <label className="az-content-label mb-0">Manage Analysis and Reporting</label>
              <span className="d-block py-2">Data Analysis</span>
            </div>
            <div className="d-flex flex-column flex-md-row gap-2 align-items-start justify-content-md-end">
              {deviceTableData.length > 0 && userDetails?.role !== userRole.public && (
                <Button danger type="primary" onClick={handleDownloadReport}>
                  Download Report
                </Button>
              )}
              <Button
                type="primary"
                icon={<RiAiGenerate />}
                onClick={() => {
                  setShow(!show);
                  setModalMode("generate");
                }}
              >
                Generate
              </Button>
              <Button
                icon={<IoCloudDownloadOutline />}
                onClick={() => {
                  setShow(!show);
                  setModalMode("bulk");
                }}
              >
                Bulk Download
              </Button>
            </div>
          </div>
          <hr className="" />

          <div className="row">
            <div className="col-md-12">
              <div className="mt-3" style={{ minHight: "350px" }}>
                {formData?.type === "graph" ? (
                  deviceData?.length > 0 ? (
                    formData?.type === "graph" && (
                      <div style={{ height: 450 }}>
                        <SplineChart data={deviceData} isLoading={isLoading} />
                      </div>
                    )
                  ) : (
                    <Empty />
                  )
                ) : deviceTableData?.length > 0 ? (
                  <div className="overflow-auto">
                    <ReportTable
                      title="Device Comparison"
                      data={deviceTableData}
                      parameters={deviceParameters.map((p) => ({ label: p }))}
                      total={total}
                      limit={limit}
                      page={page}
                      setPage={setPage}
                      setLimit={setLimit}
                      getReport={getReport}
                    />
                  </div>
                ) : (
                  <Empty />
                )}
              </div>
            </div>
          </div>

          <DeviceComparisonModal
            {...{
              mode: modalMode,
              show,
              setShow,
              formData,
              setFormData,
              getReport: modalMode === "bulk" ? handleDownloadReport : getReport,
              setDeviceData,
              setDeviceTableData,
              setDeviceParameters,
              setPage,
            }}
          />
        </div>
      </div>
    </LoadingOverlay>
  );
};

export default DataAnalysis;
